import FeatherIcon from 'feather-icons-react';
import React, { useState, useEffect, useRef } from 'react'
import { Button, Col, Nav, Offcanvas, Row, Tab } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import './sass/displayUnitEdit.scss';
import ConfigurationBox from './components/ConfigurationBox';
import EmptyDisplay from './components/EmptyDisplay';
import EditSimulateShelf from './components/EditSimulateShelf';
import { alertService } from '../../../../../../_services/alert.service';
import Buttons from '../../../../../../componentsV2/DataEntryComponents/Button/Buttons';
import FieldArrayConfirmModal from '../../../../../masterdata/fieldArray/components/FieldArrayConfirmModal';
import { measureConverter } from '../../../../../../_services/common.service';

const DisplayUnitEdit = ({
    showDUEditComponent,
    hideDUEditComponent,
    isRTL,
    dmode,
    distanceFromTop,
    mapFields,
    svgdrawDiemention,
    svgViewId,
    catDivHeight,
    zoomXRatio,
    editwrapper,
    displayRatio,
    pegBoardList,
    mapProducts,
    topProductHeights,
    onDisplayUnitDataChange,
    onProductsChange,
    onSave,
    spacefromtopsvgdraw,
    markablestackable,
    IsShowStackables,
    selectedScProductId,
    displayUOM
}) => {
    const {t} = useTranslation();
    const [showConfigurationBox, setShowConfigurationBox] = useState(false);
    const [displayUnitData, setDisplayUnitData] = useState(mapFields);
    const [displayProducts, setDisplayProducts] = useState(mapProducts);
    const initialProductsRef = useRef(null);
    const [deletedProducts, setDeletedProducts] = useState([]); // Products moved to clipboard
    const [temporaryOverflowProducts, setTemporaryOverflowProducts] = useState([]); // Temporary overflow products (not yet in clipboard)
    const [selectedShelf, setSelectedShelf] = useState(null);
    const [depthValue, setDepthValue] = useState(0);
    const [sspaceValue, setSSpaceValue] = useState(0);
    const [ffloorValue, setFFloorValue] = useState(0);
    const [thicknessValue, setThicknessValue] = useState(0);
    const [calculatedTopHeights, setCalculatedTopHeights] = useState([]);
    const [initialOverlapShelves, setInitialOverlapShelves] = useState([]);

    // Reassign product field_custom_id & shelfrank based on their x, y positions within shelf areas
    const reassignProductsToShelves = (products, unitData) => {
        if (!products || !unitData) return products;

        return products.map(product => {
            if (product.isDelete) return product; // Skip deleted products

            const productX = product.x || 0;
            const productY = product.y || 0;

            // Find which field and shelf this product belongs to based on position
            let assignedFieldCustomId = null;
            let assignedShelfRank = null;
            let assignedActualFieldId = null;

            // Iterate through all fields in unitData
            Object.keys(unitData).forEach(fieldKey => {
                const field = unitData[fieldKey];

                if (!field || !field.shelf || field.shelf.length === 0) return;

                // Check each shelf in this field
                field.shelf.forEach(shelf => {
                    // Define shelf boundaries
                    const shelfLeft = shelf.x;
                    const shelfRight = shelf.x + (shelf.drawWidth || field.drawWidth);
                    const shelfTop = shelf.y;
                    const shelfBottom = shelf.y + shelf.drawHeight;

                    // Check if product's center point is within this shelf area
                    const productCenterX = productX + (product.drawWidth || 0) / 2;
                    const productCenterY = productY + (product.drawHeight || 0) / 2;

                    const isWithinShelfX = productCenterX >= shelfLeft && productCenterX <= shelfRight;
                    const isWithinShelfY = productCenterY >= shelfTop && productCenterY <= shelfBottom;

                    if (isWithinShelfX && isWithinShelfY) {
                        assignedFieldCustomId = field.field_custom_id;
                        assignedShelfRank = shelf.rank;
                        assignedActualFieldId = field.actualFieldId;
                    }
                });
            });

            // If we found a matching shelf, update the product
            if (assignedFieldCustomId && assignedShelfRank) {
                return {
                    ...product,
                    field_custom_id: assignedFieldCustomId,
                    shelfrank: assignedShelfRank,
                    actualFieldInfo: [{
                        actualFieldId: assignedActualFieldId,
                        shelfRank: assignedShelfRank,
                        field_custom_id: assignedFieldCustomId
                    }]
                };
            }

            // If no match found, return product unchanged
            return product;
        });
    }; 


    // Delete confirmation modal state
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteModalData, setDeleteModalData] = useState({
        title: '',
        desc: '',
        callBack: () => {}
    });

    // Save confirmation modal state (for overflow products)
    const [showSaveConfirmModal, setShowSaveConfirmModal] = useState(false);
    const [saveConfirmModalData, setSaveConfirmModalData] = useState({
        title: '',
        desc: '',
        callBack: () => {}
    });

    // Unsaved changes confirmation modal state
    const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
    const [unsavedChangesModalData, setUnsavedChangesModalData] = useState({
        title: '',
        desc: '',
        callBack: () => {}
    });

    // Constants for keyboard shortcuts
    const HEIGHT_ADJUSTMENT_STEP = 1; // cm - constant value for height change
    const GAP_ADJUSTMENT_STEP = 2; // cm - constant value for gap change
    const MIN_SHELF_HEIGHT = 5; // cm - minimum height a shelf can have
    const NEW_SHELF_HEIGHT = 5; // cm - height for newly added shelf
    const ARROW_KEY_MOVEMENT_STEP = 1; // cm - movement step for arrow keys
    const PEG_BOARD_OVERLAP_THRESHOLD = 2; // pixels - threshold for pegboard overlap detection

    // Constants for step-based positioning (future: will come from backend per field)
    // In future: each field in mapFields will have shelfStartPoint and defaultGap
    const HEIGHT_ADJUSTMENT_STARTING_POINT = 0; // cm - starting point for step calculation (y=0)
    const STEP_SIZE_CM = 1; // cm - each step is 1cm (same as HEIGHT_ADJUSTMENT_STEP)

    // Reassign products to shelves based on their positions - RUN ONLY ONCE on component mount
    useEffect(() => {
        if (mapFields && mapProducts && mapProducts.length > 0) {
            const reassignedProducts = reassignProductsToShelves(mapProducts, mapFields);
            initialProductsRef.current = reassignedProducts;
            setDisplayProducts(reassignedProducts);
            setDisplayUnitData(mapFields);
        }
    }, []);

    // Notify parent component when displayUnitData changes
    useEffect(() => {
        if (onDisplayUnitDataChange && displayUnitData !== mapFields) {
            onDisplayUnitDataChange(displayUnitData);
        }
    }, [displayUnitData]);

    // Notify parent component when products change
    useEffect(() => {
        if (onProductsChange && displayProducts !== mapProducts) {
            onProductsChange(displayProducts);
        }
    }, [displayProducts]);

    // Recalculate top heights whenever displayUnitData or displayProducts change
    useEffect(() => {
        const newTopHeights = calculateTopHeights();
        setCalculatedTopHeights(newTopHeights);
    }, [displayUnitData, displayProducts, markablestackable]);

    // Define initial overlap shelf list when component mounts or pegBoardList/mapFields changes
    useEffect(() => {
        if (!pegBoardList || pegBoardList.length === 0 || !mapFields) {
            setInitialOverlapShelves([]);
            return;
        }

        const overlapList = [];

        // Check each field and shelf for initial overlap with pegboards
        Object.keys(mapFields).forEach((fieldKey) => {
            const field = mapFields[fieldKey];

            if (field.shelf && field.shelf.length > 0) {
                field.shelf.forEach((shelf) => {
                    // Check each pegboard
                    for (const pegBoard of pegBoardList) {
                        if (!pegBoard.pegDrawingRect) continue;

                        const pegRect = {
                            x: pegBoard.pegDrawingRect.drawX,
                            y: pegBoard.pegDrawingRect.drawY,
                            width: pegBoard.pegDrawingRect.drawRectWidth,
                            height: pegBoard.pegDrawingRect.drawRectHeight
                        };

                        // Check if shelf rectangle overlaps with pegboard
                        const shelfRect = {
                            x: shelf.x,
                            y: shelf.y + shelf.drawHeight,
                            width: shelf.drawWidth || field.drawWidth,
                            height: shelf.drawGap
                        };

                        if (checkRectangleOverlap(shelfRect, pegRect, PEG_BOARD_OVERLAP_THRESHOLD)) {
                            // Add to initial overlap list (use shelf.id for tracking since ranks can change)
                            overlapList.push({
                                fieldId: field.actualFieldId,
                                shelfId: shelf.id, // Use shelf ID instead of rank
                                shelfRank: shelf.rank, // Keep rank for reference only
                                pegBoardName: pegBoard.name
                            });
                            break; // No need to check other pegboards for this shelf
                        }
                    }
                });
            }
        });

        setInitialOverlapShelves(overlapList);
    }, [pegBoardList, mapFields]);

    // Snap value to nearest step
    // field: the field object (in future will contain shelfStartPoint and defaultGap)
    // value: the value to snap (in cm)
    // Returns: snapped value aligned to step grid
    const snapToStep = (field, value) => {
        // Future implementation: use field.shelfStartPoint and field.defaultGap
        // const startPoint = field?.shelfStartPoint ?? HEIGHT_ADJUSTMENT_STARTING_POINT;
        // const stepSize = field?.defaultGap ?? STEP_SIZE_CM;

        // Current implementation: use constants
        const startPoint = HEIGHT_ADJUSTMENT_STARTING_POINT;
        const stepSize = STEP_SIZE_CM;

        // Calculate offset from starting point
        const offset = value - startPoint;

        // Round to nearest step
        const snappedOffset = Math.round(offset / stepSize) * stepSize;

        // Return snapped value
        return startPoint + snappedOffset;
    };

    // Helper function to truncate decimal to 2 places (cut, not round)
    const truncateDecimal = (value, decimals = 2) => {
        const multiplier = Math.pow(10, decimals);
        return Math.trunc(value * multiplier) / multiplier;
    };

    // Check if two rectangles overlap
    // threshold: optional parameter to allow small overlaps (in pixels)
    const checkRectangleOverlap = (rect1, rect2, threshold = 0) => {
        // rect1 and rect2 should have { x, y, width, height }
        // Truncate all values to 2 decimal places
        const r1x = truncateDecimal(rect1.x);
        const r1y = truncateDecimal(rect1.y);
        const r1w = truncateDecimal(rect1.width);
        const r1h = truncateDecimal(rect1.height);
        const r2x = truncateDecimal(rect2.x);
        const r2y = truncateDecimal(rect2.y);
        const r2w = truncateDecimal(rect2.width);
        const r2h = truncateDecimal(rect2.height);

        return !(
            r1x + r1w <= r2x + threshold ||   // rect1 is to the left of rect2
            r1x >= r2x + r2w - threshold ||   // rect1 is to the right of rect2
            r1y + r1h <= r2y + threshold ||  // rect1 is above rect2
            r1y >= r2y + r2h - threshold     // rect1 is below rect2
        );
    };

    // Check if shelf (with products) would overlap with any pegboard
    const checkShelfPegboardOverlap = (field, shelf) => {
        if (!pegBoardList || pegBoardList.length === 0) {
            return false; // No pegboards to check against
        }

        // Check if this shelf was initially overlapping with a pegboard
        // Use shelf.id for comparison since ranks can change when adding/deleting shelves
        const wasInitiallyOverlapping = initialOverlapShelves.some(
            overlap => overlap.fieldId === field.actualFieldId && overlap.shelfId === shelf.id
        );

        // If shelf was initially overlapping, skip pegboard validation
        // (pegboard was drawn on top of this shelf)
        if (wasInitiallyOverlapping) {
            return false;
        }

        // Get products on this shelf
        const productsOnShelf = displayProducts.filter(product =>
            product?.actualFieldInfo &&
            product.actualFieldInfo.some(info => info.actualFieldId === field.actualFieldId) &&
            product.shelfrank === shelf.rank &&
            !product.isDelete &&
            !product?.isHanged
        );

        // Check each pegboard
        for (const pegBoard of pegBoardList) {
            if (!pegBoard.pegDrawingRect) continue;

            const pegRect = {
                x: pegBoard.pegDrawingRect.drawX,
                y: pegBoard.pegDrawingRect.drawY,
                width: pegBoard.pegDrawingRect.drawRectWidth,
                height: pegBoard.pegDrawingRect.drawRectHeight
            };

            // Check if shelf rectangle overlaps with pegboard
            const shelfRect = {
                x: shelf.x,
                y: shelf.y + shelf.drawHeight,
                width: shelf.drawWidth || field.drawWidth,
                height: shelf.drawGap
            };

            if (checkRectangleOverlap(shelfRect, pegRect, PEG_BOARD_OVERLAP_THRESHOLD)) {
                return true;
            }

            // Check if any product on this shelf would overlap with pegboard
            if (productsOnShelf.length > 0) {
                for (const product of productsOnShelf) {
                    // Calculate product position on the shelf
                    const productX = shelf.x + (product.startingPoint || 0) * displayRatio;
                    const productY = shelf.y + shelf.drawHeight - product.drawHeight;

                    const productRect = {
                        x: productX,
                        y: productY,
                        width: product.drawWidth,
                        height: product.drawHeight * (product.stackCount > 0 && markablestackable ? product.stackCount : 1)
                    };

                    if (checkRectangleOverlap(productRect, pegRect, PEG_BOARD_OVERLAP_THRESHOLD)) {
                        console.warn(`Product would overlap with pegboard ${pegBoard.name}`);
                        return true;
                    }
                }
            }
        }

        return false;
    };

    // Calculate top heights for products on each shelf
    const calculateTopHeights = () => {
        let topHeightArray = [];

        if (displayUnitData && Object.keys(displayUnitData).length > 0) {
            Object.keys(displayUnitData).forEach((fieldKey) => {
                const field = displayUnitData[fieldKey];

                if (field.shelf && field.shelf.length > 0) {
                    field.shelf.forEach((shelf) => {
                        // Filter products placed on the current shelf
                        const productsOnShelf = displayProducts.filter(product =>
                            product?.actualFieldInfo && product.actualFieldInfo.some(info => info.actualFieldId === field.actualFieldId) &&
                            product.shelfrank === shelf.rank &&
                            !product.isDelete
                        );

                        if (productsOnShelf.length > 0) {
                            // Calculate the top height considering stack count
                            let maxTopHeight = 0;
                            productsOnShelf.forEach((product) => {
                                const productHeight = product.drawHeight;
                                let totalHeight;

                                // Calculate the top position based on stack count
                                if (!product.stackCount || product.stackCount === 0) {
                                    // No stack - use product's current position
                                    totalHeight = productHeight;
                                } else if (!markablestackable && product.stackCount === 0) {
                                    // Stackable not allowed - treat as no stack
                                    totalHeight = productHeight;
                                } else if (markablestackable && product.stackCount > 0) {
                                    // Stackable allowed - calculate with stack count
                                    totalHeight = productHeight + (productHeight * product.stackCount);
                                } else {
                                    // Has stacks - calculate the top position of all stacked products
                                    // stackCount represents number of products stacked on top of the base product
                                    // product stackCount 1 means 2 products total (base + 1 stack)
                                    totalHeight = productHeight + (productHeight * product.stackCount);
                                }

                                // Find the MAXIMUM height (tallest product) on this shelf
                                if (totalHeight > maxTopHeight || maxTopHeight === 0) {
                                    maxTopHeight = totalHeight;
                                }
                            });

                            topHeightArray.push({
                                fieldId: field.actualFieldId,
                                shelfRank: shelf.rank,
                                topHeight: maxTopHeight,
                                products: productsOnShelf
                            });
                        }
                    });
                }
            });
        }

        return topHeightArray;
    };

    // Helper function to update product shelfranks
    const updateProductShelfRanks = (fieldKey, fromRank, incrementBy = 1) => {
        if (!displayProducts || !displayProducts.length) return displayProducts;

        const updatedProducts = displayProducts.map(product => {
            // Check if product belongs to the same field
            const productFieldKey = product.field_custom_id || product.fieldId;

            if (productFieldKey === fieldKey && product.shelfrank >= fromRank) {
                return {
                    ...product,
                    shelfrank: product.shelfrank + incrementBy
                };
            }
            return product;
        });

        return updatedProducts;
    };

    // Recalculate product Y positions based on updated shelf positions
    const getAdjustedProductPosition = (product) => {
        if (!product || !mapFields) return { x: product.x, y: product.y };

        // Find the field this product belongs to
        const fieldKey = product.field_custom_id || product.fieldId;
        const field = mapFields[fieldKey];

        if (!field || !field.shelf || !product.shelfrank) {
            return { x: product.x, y: product.y };
        }

        // Find the shelf this product belongs to
        const shelf = field.shelf.find(s => s.rank === product.shelfrank);

        if (!shelf) {
            return { x: product.x, y: product.y };
        }
        return {
            y: (shelf.y + shelf.drawHeight) - ( measureConverter(product.uom,displayUOM,product.height) * displayRatio), // Position product on top of shelf
            x: product.x,
            drawWidth: measureConverter(product.uom,displayUOM,product.width) * displayRatio,
            drawHeight: measureConverter(product.uom,displayUOM,product.height) * displayRatio,
        };
    };

    // Helper function to validate products across ALL shelves in displayUnitData
    const validateAllProductsAcrossAllShelves = (updatedProducts, unitData = displayUnitData, selectedFieldKey = null) => {
        const allOverflowProducts = [];

        // Get the selected field's actualFieldId to determine adjacent fields
        let targetFieldIds = [];

        if (selectedFieldKey && unitData[selectedFieldKey]) {
            const selectedField = unitData[selectedFieldKey];
            const selectedActualFieldId = selectedField.actualFieldId;

            // Get left and right adjacent field IDs
            const leftFieldId = selectedActualFieldId - 1;
            const rightFieldId = selectedActualFieldId + 1;

            // Include selected field and its left/right neighbors
            targetFieldIds = [leftFieldId, selectedActualFieldId, rightFieldId];

            console.log(`Validating products for field ${selectedActualFieldId} and adjacent fields (${leftFieldId}, ${rightFieldId})`);
        }

        // Iterate through all fields
        Object.keys(unitData).forEach(fieldKey => {
            const field = unitData[fieldKey];

            if (!field || !field.shelf || field.shelf.length === 0) return;

            // If we have target fields specified, only validate those fields
            if (targetFieldIds.length > 0 && !targetFieldIds.includes(field.actualFieldId)) {
                return; // Skip fields that are not in the target list
            }

            // Iterate through all shelves in this field
            field.shelf.forEach(shelf => {
                const shelfOverflow = validateProductsOnShelf(field, shelf, updatedProducts);
                allOverflowProducts.push(...shelfOverflow);
            });
        });

        return allOverflowProducts;
    };

    // Helper function to check if products fit within shelf area and mark them as overflow
    const validateProductsOnShelf = (field, shelf, updatedProducts) => {
        // Define shelf physical boundaries
        const shelfLeft = shelf.x;
        const shelfRight = shelf.x + (shelf.drawWidth || field.drawWidth);
        const shelfTop = shelf.y;
        const shelfBottom = shelf.y + shelf.drawHeight;

        // Get ALL products that might be within this shelf's physical area
        const productsInShelfArea = updatedProducts.filter(product => {
            if (product.isDelete || product?.isHanged) return false;

            // Get product position
            const adjustedPos = getAdjustedProductPosition(product);
            const productX = adjustedPos.x || 0;
            const productY = adjustedPos.y || 0;
            const productRight = adjustedPos.x + adjustedPos.drawWidth;
            const productBottom = adjustedPos.y + adjustedPos.drawHeight;

            // Check if product overlaps with shelf area (any overlap means conflict)
            const overlapsHorizontally = productX < shelfRight && productRight > shelfLeft;
            const overlapsVertically = productY < shelfBottom && productBottom > shelfTop;

            return overlapsHorizontally && overlapsVertically;
        });

        if (productsInShelfArea.length === 0) {
            return [];
        }

        const overflowProducts = [];

        // Check each product in the shelf area
        productsInShelfArea.forEach(product => {
            const adjustedPos = getAdjustedProductPosition(product);
            const productX = adjustedPos.x || 0;
            const productRight = productX + adjustedPos.drawWidth;

            // Check if product belongs to current shelf and field
            // const belongsToCurrentField = product.field_custom_id == shelf.field_custom_id;
            // const belongsToCurrentShelf = product.shelfrank == shelf.rank;
            // If product doesn't belong to this exact shelf+field combination, it's an overflow
            // if (!belongsToCurrentField || !belongsToCurrentShelf) {
            //     console.warn(`Product ${product.productId} is in shelf area but doesn't belong to this shelf. Will be moved to clipboard on save.`);
            //     overflowProducts.push({
            //         ...product,
            //         shelfRank: shelf.rank,
            //         fieldKey: field.actualFieldId,
            //         reason: 'wrong_position'
            //     });
            // } 
            // If product belongs to this shelf but extends beyond shelf boundaries
            if (productRight > shelfRight) {
                console.warn(`Product ${product.productId} overflows shelf width (${productRight} > ${shelfRight}). Will be moved to clipboard on save.`);
                overflowProducts.push({
                    ...product,
                    shelfRank: shelf.rank,
                    fieldKey: field.actualFieldId,
                    reason: 'overflow'
                });
            }
        });

        return overflowProducts;
    };

    const handleShelfPropertyChange = (property, value) => {
        if (!selectedShelf) return false;

        const { fieldKey, rank } = selectedShelf;

        // Create a deep copy of displayUnitData
        const updatedDisplayUnitData = JSON.parse(JSON.stringify(displayUnitData));
        const field = updatedDisplayUnitData[fieldKey];

        if (!field || !field.shelf) return false;

        // Find the selected shelf and the shelf below it
        const shelfIndex = field.shelf.findIndex(s => s.rank === rank);
        if (shelfIndex === -1) return false;

        const currentShelf = field.shelf[shelfIndex];
        const shelfBelow = shelfIndex < field.shelf.length - 1 ? field.shelf[shelfIndex + 1] : null;
        const shelfAbove = shelfIndex > 0 ? field.shelf[shelfIndex - 1] : null;

        // return when user slecet last shelf and try to increase height
        if ((property === 'height' || property === 'fromFloor') && !shelfBelow) {
            console.warn("Cannot increase height of the last shelf beyond current height.");
            alertService.error(t("CANNOT_ADJUST_LAST_SHELF"));
            return false;
        }

        // Handle height (S.Space) changes
        if (property === 'height') {
            const heightDifference = value - currentShelf.height;
            const newDrawHeight = value * displayRatio;
            const isIncreasing = heightDifference > 0;

            // Check if current shelf has products by looking in calculatedTopHeights
            const shelfHasProducts = calculatedTopHeights?.find(
                item => item.fieldId === field.actualFieldId && item.shelfRank === currentShelf.rank
            );

            if (shelfHasProducts && shelfHasProducts.topHeight) {
                const maxProductHeight = shelfHasProducts.topHeight;
                if (newDrawHeight < maxProductHeight) {
                    console.warn(`Cannot adjust height: new height (${newDrawHeight}) would be less than product height (${maxProductHeight})`);
                    alertService.error(t("CANNOT_DECREASE_HEIGHT_PRODUCTS_ON_SHELF"));
                    return false;
                }
            }

            // Check if there's a shelf below to take space from
            if (shelfBelow) {
                const newBelowHeight = shelfBelow.height - heightDifference;

                // Ensure the shelf below doesn't get too small
                if (newBelowHeight < MIN_SHELF_HEIGHT) {
                    console.warn(`Cannot adjust: shelf below would be too small (${newBelowHeight}cm < ${MIN_SHELF_HEIGHT}cm)`);
                    alertService.error(isIncreasing ? t("CANNOT_INCREASE_HEIGHT_INSUFFICIENT_SPACE") : t("CANNOT_DECREASE_HEIGHT_INSUFFICIENT_SPACE"));
                    return false;
                }
                // Also ensure the current shelf doesn't become too small
                if (value < MIN_SHELF_HEIGHT) {
                    console.warn(`Cannot adjust: current shelf height would be too small (${value}cm < ${MIN_SHELF_HEIGHT}cm)`);
                    alertService.error(isIncreasing ? t("CANNOT_INCREASE_HEIGHT_INSUFFICIENT_SPACE") : t("CANNOT_DECREASE_HEIGHT_INSUFFICIENT_SPACE"));
                    return false;
                }

                // CRITICAL: Check if shelf below has products that would be crushed
                const shelfBelowHasProducts = calculatedTopHeights?.find(
                    item => item.fieldId === field.actualFieldId && item.shelfRank === shelfBelow.rank
                );

                if (shelfBelowHasProducts && shelfBelowHasProducts.topHeight) {
                    // Shelf below has products - validate that reducing its height won't crush products
                    const maxProductHeightBelow = shelfBelowHasProducts.topHeight;
                    if (newBelowHeight * displayRatio < maxProductHeightBelow) {
                        console.warn(`Cannot adjust height: shelf below would crush products (new height: ${newBelowHeight * displayRatio}, product height: ${maxProductHeightBelow})`);
                        alertService.error(t("CANNOT_INCREASE_HEIGHT_PRODUCTS_BELOW"));
                        return false;
                    }
                }

                // Update current shelf height
                currentShelf.height = value;
                currentShelf.drawHeight = newDrawHeight;

                // Update shelf below - reduce its height
                shelfBelow.height = newBelowHeight;
                shelfBelow.drawHeight = newBelowHeight * displayRatio;
                // Recalculate Y positions for shelf below and all shelves after it
                for (let i = shelfIndex + 1; i < field.shelf.length; i++) {
                    const prevShelf = field.shelf[i - 1];
                    const currentIterShelf = field.shelf[i];

                    // Y position = previous shelf's Y + previous shelf's height + previous shelf's gap
                    currentIterShelf.y = prevShelf.y + prevShelf.drawHeight + prevShelf.drawGap;
                }
            }
        }

        // Handle gap (Thickness) changes
        if (property === 'gap') {
            const newDrawGap = value * displayRatio;
            const gapDifference = value - currentShelf.gap;
            // Validate against shelf below (if exists)
            if (shelfBelow) {
                // Calculate where the bottom of current shelf would be with new gap
                const currentShelfBottom = currentShelf.y + newDrawGap + MIN_SHELF_HEIGHT;

                // Check if new gap would cause overlap with shelf below
                if (currentShelfBottom > shelfBelow.y) {
                    console.warn(`Cannot adjust gap: would overlap with shelf below (${currentShelfBottom} > ${shelfBelow.y})`);
                    alertService.error(t("CANNOT_INCREASE_THICKNESS_OVERLAP"));
                    return false;
                }

                // Check if shelf below would be too small after gap change
                const newBelowHeight = shelfBelow.height - gapDifference;
                if (newBelowHeight < MIN_SHELF_HEIGHT) {
                    console.warn(`Cannot adjust gap: shelf below would be too small (${newBelowHeight}cm < ${MIN_SHELF_HEIGHT}cm)`);
                    alertService.error(t("CANNOT_INCREASE_THICKNESS_INSUFFICIENT_SPACE"));
                    return false;
                }

                // CRITICAL: Check if shelf below has products that would be crushed
                const shelfBelowHasProducts = calculatedTopHeights?.find(
                    item => item.fieldId === field.actualFieldId && item.shelfRank === shelfBelow.rank
                );

                if (shelfBelowHasProducts && shelfBelowHasProducts.topHeight) {
                    // Shelf below has products - validate that reducing its height won't crush products
                    const maxProductHeightBelow = shelfBelowHasProducts.topHeight;
                    if (newBelowHeight * displayRatio < maxProductHeightBelow) {
                        console.warn(`Cannot adjust gap: shelf below would crush products (new height: ${newBelowHeight * displayRatio}, product height: ${maxProductHeightBelow})`);
                        alertService.error(t("CANNOT_INCREASE_THICKNESS_PRODUCTS_BELOW"));
                        return false;
                    }
                }

                // Update shelf below height based on gap difference
                shelfBelow.height = newBelowHeight;
                shelfBelow.drawHeight = newBelowHeight * displayRatio;
                shelfBelow.y = shelfBelow.y + gapDifference * displayRatio;
            }

            // Update current shelf gap
            currentShelf.gap = value;
            currentShelf.y = currentShelf.y;
            currentShelf.drawGap = newDrawGap;
        }
        // Handle From Floor (Y) changes
        // The 'value' here is the "From Floor" distance, not the direct Y position
        // Formula: fromFloor = field.drawHeight - (shelf.y + shelf.drawHeight + shelf.drawGap)
        // When fromFloor changes, we need to adjust the current shelf's height to maintain the relationship
        if (property === 'y') {
            // Calculate the new height based on the "From Floor" value
            // fromFloor = field.drawHeight - (y + drawHeight + drawGap)
            // Rearranging: drawHeight = field.drawHeight - fromFloor - y - drawGap
            const newDrawHeight = field.drawHeight - value - (field.y - currentShelf.y) - currentShelf.drawGap;
            const newHeight = newDrawHeight / displayRatio;
            const heightDifference = newHeight - currentShelf.height;

            // Check if this shelf has products by looking in calculatedTopHeights
            const shelfHasProducts = calculatedTopHeights?.find(
                item => item.fieldId === field.actualFieldId && item.shelfRank === currentShelf.rank
            );

            if (shelfHasProducts && shelfHasProducts.topHeight) {
                const maxProductHeight = shelfHasProducts.topHeight;
                if (newDrawHeight < maxProductHeight) {
                    return false;
                }
            }

            // Check if there's a shelf below to take space from
            if (shelfBelow) {
                const newBelowHeight = shelfBelow.height - heightDifference;

                // Ensure the shelf below doesn't get too small
                if (newBelowHeight < MIN_SHELF_HEIGHT) {
                    console.warn(`Cannot adjust: shelf below would be too small (${newBelowHeight}cm < ${MIN_SHELF_HEIGHT}cm)`);
                    alertService.error(t("CANNOT_ADJUST_FROM_FLOOR_INSUFFICIENT_SPACE"));
                    return false;
                }
                if (newHeight < MIN_SHELF_HEIGHT) {
                    console.warn(`Cannot adjust: current shelf height would be too small (${newHeight}cm < ${MIN_SHELF_HEIGHT}cm)`);
                    alertService.error(t("CANNOT_ADJUST_FROM_FLOOR_SHELF_TOO_SMALL"));
                    return false;
                }

                // CRITICAL: Check if shelf below has products that would be crushed
                const shelfBelowHasProducts = calculatedTopHeights?.find(
                    item => item.fieldId === field.actualFieldId && item.shelfRank === shelfBelow.rank
                );

                if (shelfBelowHasProducts && shelfBelowHasProducts.topHeight) {
                    // Shelf below has products - validate that reducing its height won't crush products
                    const maxProductHeightBelow = shelfBelowHasProducts.topHeight;
                    if (newBelowHeight * displayRatio < maxProductHeightBelow) {
                        alertService.error(t("CANNOT_ADJUST_FROM_FLOOR_PRODUCTS_BELOW"));
                        return false;
                    }
                }

                // Update current shelf height
                currentShelf.height = newHeight;
                currentShelf.drawHeight = newDrawHeight;

                // Update shelf below - reduce its height
                shelfBelow.height = newBelowHeight;
                shelfBelow.drawHeight = newBelowHeight * displayRatio;

                // Recalculate Y positions for shelf below and all shelves after it
                for (let i = shelfIndex + 1; i < field.shelf.length; i++) {
                    const prevShelf = field.shelf[i - 1];
                    const currentIterShelf = field.shelf[i];

                    // Y position = previous shelf's Y + previous shelf's height + previous shelf's gap
                    currentIterShelf.y = prevShelf.y + prevShelf.drawHeight + prevShelf.drawGap;
                }
            }
        }
        // Handle From Floor changes
        // This calculates the correct Y position based on the "from the floor" value
        // fromFloor = sum of all shelves below (their drawHeight + drawGap)
        if(property === 'fromFloor'){
            // Calculate current fromFloor value (sum of all shelves below)
            let currentFromFloor = 0;
            for (let i = shelfIndex + 1; i < field.shelf.length; i++) {
                currentFromFloor += field.shelf[i].drawHeight + field.shelf[i].drawGap;
            }
            currentFromFloor = currentFromFloor / displayRatio;

            // Calculate the difference between new and current fromFloor
            const fromFloorDifference = value - currentFromFloor;

            // If increasing fromFloor (moving shelf UP/away from floor):
            // - Current shelf height must DECREASE
            // - Shelf below height must INCREASE
            // If decreasing fromFloor (moving shelf DOWN/toward floor):
            // - Current shelf height must INCREASE
            // - Shelf below height must DECREASE

            const newCurrentHeight = currentShelf.height - fromFloorDifference;
            const newCurrentDrawHeight = newCurrentHeight * displayRatio;

            // Check if current shelf has products
            const shelfHasProducts = calculatedTopHeights?.find(
                item => item.fieldId === field.actualFieldId && item.shelfRank === currentShelf.rank
            );

            if (shelfHasProducts && shelfHasProducts.topHeight) {
                const maxProductHeight = shelfHasProducts.topHeight;
                if (newCurrentDrawHeight < maxProductHeight) {
                    console.warn(`Cannot adjust fromFloor: new height (${newCurrentDrawHeight}) would be less than product height (${maxProductHeight})`);
                    alertService.error(t("CANNOT_DECREASE_HEIGHT_PRODUCTS_ON_SHELF"));
                    return false;
                }
            }

            // Ensure current shelf doesn't get too small
            if (newCurrentHeight < MIN_SHELF_HEIGHT) {
                console.warn(`Cannot adjust fromFloor: current shelf would be too small (${newCurrentHeight}cm < ${MIN_SHELF_HEIGHT}cm)`);
                alertService.error(t("CANNOT_ADJUST_FROM_FLOOR_SHELF_TOO_SMALL"));
                return false;
            }

            // Check if there's a shelf below
            if (shelfBelow) {
                const newBelowHeight = shelfBelow.height + fromFloorDifference;

                // Ensure shelf below doesn't get too small
                if (newBelowHeight < MIN_SHELF_HEIGHT) {
                    console.warn(`Cannot adjust fromFloor: shelf below would be too small (${newBelowHeight}cm < ${MIN_SHELF_HEIGHT}cm)`);
                    alertService.error(t("CANNOT_ADJUST_FROM_FLOOR_INSUFFICIENT_SPACE"));
                    return false;
                }

                // Check if shelf below has products that would be crushed
                const shelfBelowHasProducts = calculatedTopHeights?.find(
                    item => item.fieldId === field.actualFieldId && item.shelfRank === shelfBelow.rank
                );

                if (shelfBelowHasProducts && shelfBelowHasProducts.topHeight) {
                    const maxProductHeightBelow = shelfBelowHasProducts.topHeight;
                    if (newBelowHeight * displayRatio < maxProductHeightBelow) {
                        console.warn(`Cannot adjust fromFloor: shelf below would crush products (new height: ${newBelowHeight * displayRatio}, product height: ${maxProductHeightBelow})`);
                        alertService.error(t("CANNOT_ADJUST_FROM_FLOOR_PRODUCTS_BELOW"));
                        return false;
                    }
                }

                // Update current shelf height (decreases when fromFloor increases)
                currentShelf.height = newCurrentHeight;
                currentShelf.drawHeight = newCurrentDrawHeight;

                // Update shelf below height (increases when fromFloor increases)
                shelfBelow.height = newBelowHeight;
                shelfBelow.drawHeight = newBelowHeight * displayRatio;

                // Recalculate Y positions for shelf below and all shelves after it
                for (let i = shelfIndex + 1; i < field.shelf.length; i++) {
                    const prevShelf = field.shelf[i - 1];
                    const currentIterShelf = field.shelf[i];

                    // Y position = previous shelf's Y + previous shelf's height + previous shelf's gap
                    currentIterShelf.y = prevShelf.y + prevShelf.drawHeight + prevShelf.drawGap;
                }
            } else {
                // No shelf below - cannot adjust fromFloor for last shelf
                console.warn("Cannot adjust fromFloor: no shelf below exists");
                alertService.error(t("CANNOT_ADJUST_LAST_SHELF"));
                return false;
            }
        }

        // CRITICAL: Check for pegboard overlap before applying changes
        // We need to check the current shelf and all affected shelves
        if (checkShelfPegboardOverlap(field, currentShelf)) {
            console.warn("Cannot move shelf: would overlap with pegboard");
            alertService.error(t("CANNOT_ADJUST_SHELF_PEGBOARD_OVERLAP"));
            return false;
        }

        // If we modified shelf below, check it too
        if (shelfBelow && (property === 'height' || property === 'gap' || property === 'fromFloor')) {
            if (checkShelfPegboardOverlap(field, shelfBelow)) {
                console.warn("Cannot move shelf: shelf below would overlap with pegboard");
                alertService.error(t("CANNOT_ADJUST_SHELF_PEGBOARD_OVERLAP"));
                return false;
            }
        }

        // Check all shelves that were repositioned
        if (property === 'height' || property === 'gap' || property === 'fromFloor') {
            for (let i = shelfIndex + 1; i < field.shelf.length; i++) {
                if (checkShelfPegboardOverlap(field, field.shelf[i])) {
                    console.warn(`Cannot move shelf: shelf rank ${field.shelf[i].rank} would overlap with pegboard`);
                    alertService.error(t("CANNOT_ADJUST_SHELF_PEGBOARD_OVERLAP"));
                    return false;
                }
            }
        }

        // CRITICAL: Check if products fit within selected shelf and adjacent fields only
        // This will catch products that might be displaced to neighboring shelf areas
        const allOverflowProducts = validateAllProductsAcrossAllShelves(displayProducts, updatedDisplayUnitData, fieldKey);

        // Update temporary overflow products with all detected overflows
        if (allOverflowProducts.length > 0) {
            console.warn(`Found ${allOverflowProducts.length} overflow product(s) across all shelves. Will be moved to clipboard on save.`);
            setTemporaryOverflowProducts(allOverflowProducts);
        } else {
            // No overflow products detected, clear temporary overflow
            setTemporaryOverflowProducts([]);
        }

        // Update the state
        setDisplayUnitData(updatedDisplayUnitData);

        // Update selectedShelf with the new data
        setSelectedShelf({
            ...selectedShelf,
            shelf: currentShelf,
            field: field
        });

        // Update the local state values
        if (property === 'height') {
            setSSpaceValue(value);
            const newFromFloor = field.drawHeight - (Math.abs(field.y - currentShelf.y) + currentShelf.drawHeight + currentShelf.drawGap);
            setFFloorValue(newFromFloor > 0 ? newFromFloor/displayRatio : 0);
        } else if (property === 'gap') {
            setThicknessValue(value);
        } else if (property === 'width') {
            setDepthValue(value);
        } else if (property === 'fromFloor') {
            // Update the fromFloor display value
            setFFloorValue(value);
            // Update the height value since current shelf height changed
            setSSpaceValue(currentShelf.height);
        }

        return true;
    }

    // Handle adding a new shelf above the selected shelf
    const handleAddShelfAbove = () => {
        if (!selectedShelf) {
            console.warn("No shelf selected");
            return false;
        }

        const { fieldKey, rank } = selectedShelf;

        // Create a deep copy of displayUnitData
        const updatedDisplayUnitData = JSON.parse(JSON.stringify(displayUnitData));
        const field = updatedDisplayUnitData[fieldKey];

        if (!field || !field.shelf) return false;

        // Find the selected shelf
        const shelfIndex = field.shelf.findIndex(s => s.rank === rank);
        if (shelfIndex === -1) return false;

        const currentShelf = field.shelf[shelfIndex];

        // Check if current shelf has enough height to split
        // if (currentShelf.height < NEW_SHELF_HEIGHT * 2) {
        //     console.warn(`Cannot add shelf: current shelf height (${currentShelf.height}cm) is too small to split`);
        //     alertService.error(t("CANNOT_ADD_SHELF_INSUFFICIENT_HEIGHT"));
        //     return false;
        // }

        // const minRequiredHeight = NEW_SHELF_HEIGHT + shelfBelow.gap + NEW_SHELF_HEIGHT;
        // if (shelfBelow.height < minRequiredHeight) {
        //     console.warn(`Cannot add shelf: shelf below height (${shelfBelow.height}cm) is too small to split (requires ${minRequiredHeight}cm)`);
        //     alertService.error(t("CANNOT_ADD_SHELF_INSUFFICIENT_HEIGHT"));
        //     return false;
        // }

        // Calculate current top heights for all shelves with products
        const currentTopHeights = calculateTopHeights();

        // Check if current shelf has products
        const shelfHasProducts = currentTopHeights?.find(
            item => item.fieldId === field.actualFieldId && item.shelfRank === currentShelf.rank
        );

        // First, check if current shelf has products and calculate minimum required height
        let minRequiredHeightForProducts = NEW_SHELF_HEIGHT; // Default minimum

        if (shelfHasProducts && shelfHasProducts.topHeight) {
            // Current shelf has products - need to ensure remaining height accommodates products
            // topHeight = maximum product height on this shelf (in draw units)
            const maxProductHeight = shelfHasProducts.topHeight;
            minRequiredHeightForProducts = Math.max(maxProductHeight / displayRatio, NEW_SHELF_HEIGHT);
        }

        // Calculate the new shelf height based on available space
        // The new shelf should use the current shelf's height if there's enough space
        // Otherwise, use HEIGHT_ADJUSTMENT_STEP increments or NEW_SHELF_HEIGHT as minimum
        let newShelfHeight = NEW_SHELF_HEIGHT;

        // Calculate available space for the new shelf (accounting for gap and minimum required remaining space)
        // const availableSpace = currentShelf.height - (minRequiredHeightForProducts + currentShelf.gap);
        const availableSpace =
        Math.round(
            (currentShelf.height - (minRequiredHeightForProducts + currentShelf.gap)) * 100
        ) / 100;

        // Check if there's enough space to add a shelf
        if (availableSpace < NEW_SHELF_HEIGHT) {
            console.warn(`Cannot add shelf above: insufficient space (available: ${availableSpace}cm, required: ${NEW_SHELF_HEIGHT}cm)`);
            // Show appropriate error message based on whether shelf has products
            if (shelfHasProducts && shelfHasProducts.topHeight) {
                alertService.error(t("CANNOT_ADD_SHELF_INSUFFICIENT_SPACE_FOR_PRODUCTS"));
            } else {
                alertService.error(t("CANNOT_ADD_SHELF_INSUFFICIENT_HEIGHT"));
            }
            return false;
        }

        if (availableSpace >= currentShelf.height) {
            // If there's enough space to match the current shelf height, use it
            newShelfHeight = currentShelf.height;
        } else if (availableSpace >= NEW_SHELF_HEIGHT) {
            // Calculate how many HEIGHT_ADJUSTMENT_STEP increments fit in the available space
            // const possibleSteps = Math.floor(availableSpace / HEIGHT_ADJUSTMENT_STEP);
            const possibleSteps = Math.floor(
                Math.round(availableSpace * 100) / 100 / HEIGHT_ADJUSTMENT_STEP
            );

            if (possibleSteps > 0) {
                newShelfHeight = Math.min(possibleSteps * HEIGHT_ADJUSTMENT_STEP, availableSpace);
            }
            // Ensure it's at least the minimum
            if (newShelfHeight < NEW_SHELF_HEIGHT) {

                newShelfHeight = NEW_SHELF_HEIGHT;
            }
        }

        // Create new shelf with same properties as selected shelf
        const remainingHeight = currentShelf.height - (newShelfHeight + currentShelf.gap); // Current shelf gets the remaining height

        const newShelf = {
            ...currentShelf,
            id: uuidv4(), // Generate unique ID for new shelf
            rank: currentShelf.rank, // New shelf takes current rank
            height: newShelfHeight,
            drawHeight: newShelfHeight * displayRatio,
            y: currentShelf.y, // New shelf takes current Y position
            isNew: true // Mark as newly added shelf
        };

        // Update current shelf (reduce height and move down)
        currentShelf.height = remainingHeight;
        currentShelf.drawHeight = remainingHeight * displayRatio;
        currentShelf.y = currentShelf.y + newShelf.drawHeight + newShelf.drawGap;
        currentShelf.rank = currentShelf.rank + 1; // Current shelf moves to next rank

        // Shift ranks for all shelves below
        for (let i = shelfIndex + 1; i < field.shelf.length; i++) {
            field.shelf[i].rank += 1;
        }

        // Insert new shelf at the current position
        field.shelf.splice(shelfIndex, 0, newShelf);

        // Recalculate Y positions for all shelves after the new shelf
        for (let i = shelfIndex + 1; i < field.shelf.length; i++) {
            const prevShelf = field.shelf[i - 1];
            const shelf = field.shelf[i];
            shelf.y = prevShelf.y + prevShelf.drawHeight + prevShelf.drawGap;
        }

        // Validate: Last shelf's bottom position should not exceed field height
        const lastShelf = field.shelf[field.shelf.length - 1];
        const lastShelfBottom = (field.y - lastShelf.y) + lastShelf.drawHeight + lastShelf.drawGap;
        if (lastShelfBottom > field.drawHeight) {
            console.warn(`Cannot add shelf: last shelf would exceed field height (${lastShelfBottom} > ${field.drawHeight})`);
            alertService.error(t("CANNOT_ADD_SHELF_EXCEEDS_FIELD_HEIGHT"));
            return false;
        }

        // CRITICAL: Check for pegboard overlap before adding shelf
        // Check the new shelf
        if (checkShelfPegboardOverlap(field, newShelf)) {
            console.warn("Cannot add shelf above: new shelf would overlap with pegboard");
            alertService.error(t("CANNOT_ADD_SHELF_PEGBOARD_OVERLAP"));
            return false;
        }

        // Check the modified current shelf (now moved down)
        if (checkShelfPegboardOverlap(field, currentShelf)) {
            console.warn("Cannot add shelf above: existing shelf would overlap with pegboard after repositioning");
            alertService.error(t("CANNOT_ADD_SHELF_PEGBOARD_OVERLAP"));
            return false;
        }

        // Check all shelves after the current one
        for (let i = shelfIndex + 1; i < field.shelf.length; i++) {
            if (checkShelfPegboardOverlap(field, field.shelf[i])) {
                console.warn(`Cannot add shelf above: shelf rank ${field.shelf[i].rank} would overlap with pegboard`);
                alertService.error(t("CANNOT_ADD_SHELF_PEGBOARD_OVERLAP"));
                return false;
            }
        }

        // Update product shelfranks for current shelf and all shelves below
        // Since new shelf takes the current rank, products on current shelf and below need to shift up by 1
        const updatedProducts = updateProductShelfRanks(fieldKey, rank, 1);
        setDisplayProducts(updatedProducts);

        // Update the state
        setDisplayUnitData(updatedDisplayUnitData);

        // Update selectedShelf to the newly added shelf
        setSelectedShelf({
            fieldKey,
            rank: newShelf.rank,
            shelf: newShelf,
            field: field
        });

        // Update input values
        setDepthValue(field.depth);
        setSSpaceValue(newShelf.height);
        setThicknessValue(newShelf.gap);

        const originalFromFloor = field.drawHeight - (Math.abs(field.y - newShelf.y) + newShelf.drawHeight + newShelf.drawGap);
        setFFloorValue(originalFromFloor > 0 ? originalFromFloor/displayRatio : 0);
        return true;
    };

    // Handle adding a new shelf below the selected shelf
    const handleAddShelfBelow = () => {
        if (!selectedShelf) {
            console.warn("No shelf selected");
            return false;
        }

        const { fieldKey, rank } = selectedShelf;

        // Create a deep copy of displayUnitData
        const updatedDisplayUnitData = JSON.parse(JSON.stringify(displayUnitData));
        const field = updatedDisplayUnitData[fieldKey];

        if (!field || !field.shelf) return false;

        // Find the selected shelf
        const shelfIndex = field.shelf.findIndex(s => s.rank === rank);
        if (shelfIndex === -1) return false;

        const currentShelf = field.shelf[shelfIndex];
        const shelfBelow = shelfIndex < field.shelf.length - 1 ? field.shelf[shelfIndex + 1] : null;

        // Check if there's a shelf below
        if (!shelfBelow) {
            console.warn("Cannot add shelf below: no shelf below exists");
            alertService.error(t("CANNOT_ADD_SHELF_BELOW_NO_SHELF"));
            return false;
        }

        // Check if shelf below has enough height to split
        // Need space for: new shelf height + gap + minimum height for shelf below
        const minRequiredHeight = NEW_SHELF_HEIGHT + shelfBelow.gap + NEW_SHELF_HEIGHT;
        if (shelfBelow.height < minRequiredHeight) {
            console.warn(`Cannot add shelf: shelf below height (${shelfBelow.height}cm) is too small to split (requires ${minRequiredHeight}cm)`);
            alertService.error(t("CANNOT_ADD_SHELF_INSUFFICIENT_HEIGHT"));
            return false;
        }

        // Calculate current top heights for all shelves with products
        const currentTopHeights = calculateTopHeights();

        // Check if shelf below has products
        const shelfBelowHasProducts = currentTopHeights?.find(
            item => item.fieldId === field.actualFieldId && item.shelfRank === shelfBelow.rank
        );

        if (shelfBelowHasProducts && shelfBelowHasProducts.topHeight) {
            // Shelf below has products - validate that splitting won't make remaining height too small
            // topHeight = maximum product height on shelf below (in draw units)
            const maxProductHeight = shelfBelowHasProducts.topHeight;
            const newShelfHeight = NEW_SHELF_HEIGHT;

            // Calculate remaining height of shelf below after adding new shelf (in draw units)
            const remainingDrawHeight = (shelfBelow.height - (newShelfHeight + shelfBelow.gap)) * displayRatio;

            // Compare in draw units - remaining height must be greater than or equal to tallest product height
            if (remainingDrawHeight < maxProductHeight) {
                console.warn(`Cannot add shelf below: remaining shelf below height (${remainingDrawHeight / displayRatio}cm) would be less than tallest product height (${maxProductHeight / displayRatio}cm)`);
                // This error is specifically about products, so use the products message
                alertService.error(t("CANNOT_ADD_SHELF_INSUFFICIENT_SPACE_FOR_PRODUCTS"));
                return false;
            }

            // Also verify remaining height meets minimum shelf height requirement
            const remainingHeight = shelfBelow.height - (newShelfHeight + shelfBelow.gap);
            if (remainingHeight < NEW_SHELF_HEIGHT) {
                console.warn(`Cannot add shelf below: remaining shelf below height (${remainingHeight}cm) would be less than minimum required (${NEW_SHELF_HEIGHT}cm)`);
                alertService.error(t("CANNOT_ADD_SHELF_INSUFFICIENT_HEIGHT"));
                return false;
            }
        }

        // Create new shelf with same properties as selected shelf
        const newShelfHeight = NEW_SHELF_HEIGHT; // New shelf gets minimum height
        const remainingHeight = shelfBelow.height - (newShelfHeight + shelfBelow.gap); // Shelf below gets the remaining height

        const newShelf = {
            ...currentShelf,
            id: uuidv4(), // Generate unique ID for new shelf
            rank: currentShelf.rank + 1, // New shelf goes after current
            height: newShelfHeight,
            drawHeight: newShelfHeight * displayRatio,
            // New shelf's gap inherits from shelfBelow (the space it will occupy above shelfBelow)
            gap: shelfBelow.gap,
            drawGap: shelfBelow.drawGap,
            y: currentShelf.y + currentShelf.drawHeight + currentShelf.drawGap,
            isNew: true // Mark as newly added shelf
        };

        // Update shelf below (reduce height and move down)
        shelfBelow.height = remainingHeight;
        shelfBelow.drawHeight = remainingHeight * displayRatio;
        shelfBelow.rank = shelfBelow.rank + 1; // Shift rank

        // Shift ranks for all shelves below the new shelf
        for (let i = shelfIndex + 2; i < field.shelf.length; i++) {
            field.shelf[i].rank += 1;
        }

        // Insert new shelf after the current shelf
        field.shelf.splice(shelfIndex + 1, 0, newShelf);

        // Recalculate Y positions for shelf below and all shelves after it
        for (let i = shelfIndex + 2; i < field.shelf.length; i++) {
            const prevShelf = field.shelf[i - 1];
            const shelf = field.shelf[i];
            shelf.y = prevShelf.y + prevShelf.drawHeight + prevShelf.drawGap;
        }

        // Validate: Last shelf's bottom position should not exceed field height
        const lastShelf = field.shelf[field.shelf.length - 1];
        const lastShelfBottom = (field.y - lastShelf.y) + lastShelf.drawHeight + lastShelf.drawGap;
        if (lastShelfBottom > field.drawHeight) {
            console.warn(`Cannot add shelf: last shelf would exceed field height (${lastShelfBottom} > ${field.drawHeight})`);
            alertService.error(t("CANNOT_ADD_SHELF_EXCEEDS_FIELD_HEIGHT"));
            return false;
        }

        // CRITICAL: Check for pegboard overlap before adding shelf
        // Check the new shelf
        if (checkShelfPegboardOverlap(field, newShelf)) {
            console.warn("Cannot add shelf below: new shelf would overlap with pegboard");
            alertService.error(t("CANNOT_ADD_SHELF_PEGBOARD_OVERLAP"));
            return false;
        }

        // Check the modified shelf below (now repositioned)
        if (checkShelfPegboardOverlap(field, shelfBelow)) {
            console.warn("Cannot add shelf below: existing shelf would overlap with pegboard after repositioning");
            alertService.error(t("CANNOT_ADD_SHELF_PEGBOARD_OVERLAP"));
            return false;
        }

        // Check all shelves after the new shelf
        for (let i = shelfIndex + 2; i < field.shelf.length; i++) {
            if (checkShelfPegboardOverlap(field, field.shelf[i])) {
                console.warn(`Cannot add shelf below: shelf rank ${field.shelf[i].rank} would overlap with pegboard`);
                alertService.error(t("CANNOT_ADD_SHELF_PEGBOARD_OVERLAP"));
                return false;
            }
        }

        // Update product shelfranks for shelves below the current shelf
        // New shelf goes at rank+1, so products at rank+1 and below need to shift up by 1
        const updatedProducts = updateProductShelfRanks(fieldKey, rank + 1, 1);
        setDisplayProducts(updatedProducts);

        // Update the state
        setDisplayUnitData(updatedDisplayUnitData);

        // Update selectedShelf to the newly added shelf
        setSelectedShelf({
            fieldKey,
            rank: newShelf.rank,
            shelf: newShelf,
            field: field
        });

        // Update input values
        setDepthValue(field.depth);
        setSSpaceValue(newShelf.height);
        setThicknessValue(newShelf.gap);
        const originalFromFloor = field.drawHeight - (Math.abs(field.y - newShelf.y) + newShelf.drawHeight + newShelf.drawGap);
        setFFloorValue(originalFromFloor > 0 ? originalFromFloor/displayRatio : 0);

        return true;
    };

    // Handle deleting the selected shelf
    const handleDeleteShelf = () => {
        if (!selectedShelf) {
            console.warn("No shelf selected");
            return false;
        }

        const { fieldKey, rank } = selectedShelf;

        // find the last shelf rank for this field
        const lastShelfRank = displayUnitData[fieldKey]?.shelf?.length;

        if(rank === lastShelfRank){
            alertService.error(t("CANNOT_DELETE_BOTTOM_SHELF"));
            return false;
        }

        // Cannot delete the last shelf
        const field = displayUnitData[fieldKey];
        if (!field || !field.shelf || field.shelf.length === 1) {
            alertService.error(t("CANNOT_DELETE_LAST_SHELF"));
            return false;
        }

        // Check if shelf has products
        const shelfProducts = displayProducts.filter(product => {
            // skip already deleted product
            if (product?.isDelete || product?.isHanged) return false;
            const productFieldKey = product.field_custom_id || product.fieldId;
            return productFieldKey === fieldKey && product.shelfrank === rank;
        });

        // If shelf has products, show confirmation modal
        if (shelfProducts.length > 0) {
            setDeleteModalData({
                title: t('DELETE_SHELF_CONFIRMATION'),
                desc: t('DELETE_SHELF_WITH_PRODUCTS_DESC', { count: shelfProducts.length }),
                callBack: () => {
                    confirmDeleteShelf(fieldKey, rank, shelfProducts);
                    setShowDeleteModal(false);
                }
            });
            setShowDeleteModal(true);
        } else {
            // No products, delete directly
            confirmDeleteShelf(fieldKey, rank, []);
        }
    };

    // Confirm and execute shelf deletion
    const confirmDeleteShelf = (fieldKey, rank, shelfProducts) => {
        let productsToUpdate = displayProducts;

        // If shelf has products, handle them
        if (shelfProducts.length > 0) {
            // Mark products as deleted and move to clipboard
            const productsToDelete = shelfProducts.map(product => ({
                ...product,
                isDelete: true
            }));

            // Add to deleted products state - group by shelf
            // Each shelf's products will be added as a separate group with metadata
            setDeletedProducts(prevDeleted => [
                ...prevDeleted,
                {
                    shelfRank: rank,
                    fieldKey: fieldKey,
                    products: productsToDelete,
                    timestamp: Date.now() // To maintain order of deletion
                }
            ]);

            // Remove from display products
            productsToUpdate = displayProducts.filter(product => {
                const productFieldKey = product.field_custom_id || product.fieldId;
                return !(productFieldKey === fieldKey && product.shelfrank === rank);
            });
        }

        // Update product shelfranks for products on shelves after the deleted shelf
        // This should happen whether the deleted shelf had products or not
        const updatedProducts = productsToUpdate.map(product => {
            const productFieldKey = product.field_custom_id || product.fieldId;

            if (productFieldKey === fieldKey && product.shelfrank > rank) {
                return {
                    ...product,
                    shelfrank: product.shelfrank - 1
                };
            }
            return product;
        });

        setDisplayProducts(updatedProducts);

        // Create a deep copy of displayUnitData
        const updatedDisplayUnitData = JSON.parse(JSON.stringify(displayUnitData));
        const field = updatedDisplayUnitData[fieldKey];

        if (!field || !field.shelf) return false;

        // Find the selected shelf
        const shelfIndex = field.shelf.findIndex(s => s.rank === rank);
        if (shelfIndex === -1) return false;

        const currentShelf = field.shelf[shelfIndex];
        const shelfBelow = shelfIndex < field.shelf.length - 1 ? field.shelf[shelfIndex + 1] : null;

        // If there's a shelf below, redistribute the deleted shelf's space
        if (shelfBelow) {
            // Add deleted shelf's height and gap to the shelf below
            const deletedShelfTotalHeight = currentShelf.height + currentShelf.gap;
            shelfBelow.height += deletedShelfTotalHeight;
            shelfBelow.drawHeight = shelfBelow.height * displayRatio;
            // Shelf below takes the Y position of the deleted shelf
            shelfBelow.y = currentShelf.y;
        } else {
            // If deleting the bottom shelf, add its space to the shelf above
            const shelfAbove = field.shelf[shelfIndex - 1];
            if (shelfAbove) {
                const deletedShelfTotalHeight = currentShelf.height + currentShelf.gap;
                shelfAbove.height += deletedShelfTotalHeight;
                shelfAbove.drawHeight = shelfAbove.height * displayRatio;
            }
        }

        // Remove the shelf from the array
        field.shelf.splice(shelfIndex, 1);

        // Update ranks for all shelves after the deleted shelf
        for (let i = shelfIndex; i < field.shelf.length; i++) {
            field.shelf[i].rank -= 1;
        }

        // Recalculate Y positions for all shelves after the deletion point
        for (let i = shelfIndex; i < field.shelf.length; i++) {
            if (i === 0) {
                // First shelf starts at field's Y position
                field.shelf[i].y = field.y;
            } else {
                const prevShelf = field.shelf[i - 1];
                field.shelf[i].y = prevShelf.y + prevShelf.drawHeight + prevShelf.drawGap;
            }
        }

        // Update the state
        setDisplayUnitData(updatedDisplayUnitData);

        // Clear selected shelf
        setSelectedShelf(null);
        setShowConfigurationBox(false);

        // Reset input values
        setDepthValue(0);
        setSSpaceValue(0);
        setThicknessValue(0);
        setFFloorValue(0);

        return true;
    };

    // Helper function to check if there are unsaved changes
    const hasUnsavedChanges = () => {
        const hasFieldChanges = JSON.stringify(displayUnitData) !== JSON.stringify(mapFields);
        const baseline = initialProductsRef.current ?? mapProducts;
        const hasProductChanges = JSON.stringify(displayProducts) !== JSON.stringify(baseline);
        const hasDeletedProducts = deletedProducts.length > 0;
        const hasOverflowProducts = temporaryOverflowProducts.length > 0;

        return hasFieldChanges || hasProductChanges || hasDeletedProducts || hasOverflowProducts;
    };

    // Handle close button click - check for unsaved changes
    const handleClose = () => {
        if (hasUnsavedChanges()) {
            // Show unsaved changes confirmation modal
            setUnsavedChangesModalData({
                title: t('UNSAVED_CHANGES'),
                desc: t('YOU_HAVE_UNSAVED_CHANGES_SURE_TO_LEAVE'),
                callBack: () => {
                    setShowUnsavedChangesModal(false);
                    hideDUEditComponent();
                }
            });
            setShowUnsavedChangesModal(true);
        } else {
            // No unsaved changes, close directly
            hideDUEditComponent();
        }
    };

    // Handle save button click
    const handleSave = () => {
        // If no changes at all, show alert and return
        if (!hasUnsavedChanges()) {
            alertService.warn(t('NO_CHANGES_AVAILABLE'));
            return;
        }

        // Check if there are overflow products that need to be moved to clipboard
        if (temporaryOverflowProducts.length > 0) {
            // Show confirmation modal
            setSaveConfirmModalData({
                title: 'OVERFLOW_PRODUCTS_WARNING',
                desc: t('OVERFLOW_PRODUCTS_DESC', { count: temporaryOverflowProducts.length }),
                callBack: () => {
                    confirmSaveWithOverflow();
                    setShowSaveConfirmModal(false);
                }
            });
            setShowSaveConfirmModal(true);
        } else {
            // No overflow products, proceed with normal save
            proceedWithSave();
        }
    };

    // Confirm save with overflow products and move them to clipboard
    const confirmSaveWithOverflow = () => {
        // Mark overflow products as deleted in displayProducts
        const updatedProducts = displayProducts.map(product => {
            const isOverflow = temporaryOverflowProducts.some(
                op => op.id === product.id
            );
            if (isOverflow) {
                return { ...product, isDelete: true };
            }
            return product;
        });

        // Group overflow products by shelf for clipboard
        const groupedOverflowProducts = {};
        temporaryOverflowProducts.forEach(product => {
            const key = `${product.fieldKey}_${product.shelfRank}`;
            if (!groupedOverflowProducts[key]) {
                groupedOverflowProducts[key] = {
                    shelfRank: product.shelfRank,
                    fieldKey: product.fieldKey,
                    products: [],
                    timestamp: Date.now(),
                    reason: 'overflow'
                };
            }
            groupedOverflowProducts[key].products.push({
                ...product,
                isDelete: true
            });
        });

        // Add overflow products to clipboard
        const newDeletedProducts = [...deletedProducts, ...Object.values(groupedOverflowProducts)];

        // Proceed with save
        if (onSave) {
            onSave({
                mapFields: displayUnitData,
                mapProducts: updatedProducts,
                deletedProducts: newDeletedProducts
            });
        }

        // Close the DU edit modal
        hideDUEditComponent();
    };

    // Proceed with normal save (no overflow products)
    const proceedWithSave = () => {
        // Call onSave callback with updated data
        if (onSave) {
            onSave({
                mapFields: displayUnitData,
                mapProducts: displayProducts,
                deletedProducts: deletedProducts
            });
        }

        // Close the DU edit modal
        hideDUEditComponent();
    };

    // Handle keyboard arrow key movement for selected shelf and ESC key
    useEffect(() => {
        const handleKeyDown = (event) => {
            // Handle ESC key to close the DU edit panel
            if (event.key === 'Escape') {
                // Check if any modal is currently open - if so, stop propagation
                // and let the modal handle its own closing
                if (showDeleteModal || showSaveConfirmModal || showUnsavedChangesModal) {
                    event.stopPropagation(); // Prevent parent from handling this event
                    return;
                }

                event.preventDefault();
                event.stopPropagation(); // Prevent parent from handling this event
                handleClose();
                return;
            }

            // Only handle arrow keys when a shelf is selected
            if (!selectedShelf) return;

            // Check if it's an arrow key
            if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;

            // Prevent default scroll behavior
            event.preventDefault();

            const { fieldKey, rank } = selectedShelf;
            const field = displayUnitData[fieldKey];

            if (!field || !field.shelf) return;

            // Find the selected shelf
            const shelfIndex = field.shelf.findIndex(s => s.rank === rank);
            if (shelfIndex === -1) return;

            const isLastShelf = shelfIndex === field.shelf.length - 1;

            // Cannot move the last shelf
            if (isLastShelf) {
                console.warn('Cannot move the last shelf with arrow keys');
                return;
            }

            // Calculate movement direction (in cm)
            // ArrowUp = move up = increase fromFloor value (away from floor)
            // ArrowDown = move down = decrease fromFloor value (toward floor)
            const deltaYInCm = event.key === 'ArrowUp' ? -ARROW_KEY_MOVEMENT_STEP : ARROW_KEY_MOVEMENT_STEP;

            // Calculate current fromFloor value (sum of all shelves below)
            let currentFromFloor = 0;
            for (let i = shelfIndex + 1; i < field.shelf.length; i++) {
                currentFromFloor += field.shelf[i].drawHeight + field.shelf[i].drawGap;
            }
            currentFromFloor = currentFromFloor / displayRatio;

            // When moving down (deltaY positive), fromFloor decreases
            // When moving up (deltaY negative), fromFloor increases
            const newFromFloor = currentFromFloor - deltaYInCm;

            // Validate and apply the change using the existing handler
            if (newFromFloor > 0) {
                handleShelfPropertyChange('fromFloor', newFromFloor);
            }
        };

        // Add event listener with capture phase to intercept ESC before it reaches modals
        window.addEventListener('keydown', handleKeyDown, true);

        // Cleanup
        return () => {
            window.removeEventListener('keydown', handleKeyDown, true);
        };
    }, [selectedShelf, displayUnitData, handleClose, showDeleteModal, showSaveConfirmModal, showUnsavedChangesModal]);

    return (
        <React.Fragment>
            <Offcanvas
                show={showDUEditComponent}
                onHide = {handleClose}
                placement={isRTL === "rtl"?"start":"end"}
                backdrop={false}
                scroll={true}
                className="du-panel du-fullscreen-offcanvas"
                style={{zIndex : 1060}}
                >
                <Offcanvas.Body className='du-offcanvas-body'>
                    <div className={`du-container ${isRTL === "rtl" ? "du-container-rtl" : "du-container-ltr"}`}>
                        <div className="du-left">
                            {/* Left content */}
                            <EditSimulateShelf
                                isRTL = {isRTL}
                                dmode = {dmode}
                                distanceFromTop = {distanceFromTop}
                                mapFields = {displayUnitData}
                                svgdrawDiemention = {svgdrawDiemention}
                                svgViewId = {svgViewId}
                                catDivHeight = {catDivHeight}
                                wrapperId = "simulation-edit-wrapper"
                                setShowConfigurationBox = {setShowConfigurationBox}
                                setDisplayUnitData = {setDisplayUnitData}
                                selectedShelf = {selectedShelf}
                                setSelectedShelf = {setSelectedShelf}
                                setDepthValue = {setDepthValue}
                                setSSpaceValue = {setSSpaceValue}
                                setFFloorValue = {setFFloorValue}
                                setThicknessValue = {setThicknessValue}
                                onShelfClick = {setSelectedShelf}
                                pegBoardList = {pegBoardList}
                                mapProducts = {displayProducts}
                                topProductHeights = {calculatedTopHeights}
                                onShelfPropertyChange = {handleShelfPropertyChange}
                                displayRatio = {displayRatio}
                                spacefromtopsvgdraw = {spacefromtopsvgdraw}
                                IsShowStackables = {IsShowStackables}
                                selectedScProductId = {selectedScProductId}
                                displayUOM={displayUOM}
                                stepStartPoint={HEIGHT_ADJUSTMENT_STARTING_POINT}
                                stepSize={STEP_SIZE_CM}
                            />

                        </div>
                        <div className="du-right">
                            {/* Right content */}
                            <Offcanvas.Header className={isRTL === "rtl" ? "du-header-rtl" : "du-header-ltr"}>
                                <Buttons
                                    className={`du-panel-close rounded-circle`}
                                    size="sm"
                                    clickEvent={handleClose}
                                    variant="secondary"
                                    dir={isRTL}
                                    btnIcon={isRTL === "ltr" ? <FeatherIcon icon="chevron-right" size={20} /> : <FeatherIcon icon="chevron-left" size={20} />}
                                />
                                <Offcanvas.Title>
                                    {isRTL === "rtl" ? (
                                        <><strong>{t('DISPLAY_UNITS')}</strong> <FeatherIcon icon="tool" size={18} /></>
                                    ) : (
                                        <><FeatherIcon icon="tool" size={18} /> <strong>{t('DISPLAY_UNITS')}</strong></>
                                    )}
                                    <p className='du-tagline'>{t('DISPLAY_UNITS_TAGLINE')}</p>
                                </Offcanvas.Title>
                            </Offcanvas.Header>
                            <div className="du-right-body">
                                {/* Body content */}
                                <Row className={`du-tab-row ${isRTL === "rtl" ? "du-tab-row-rtl" : "du-tab-row-ltr"}`}>
                                    <Col xs="12">
                                        <Tab.Container defaultActiveKey="shelves">
                                            <Row>
                                                <Col>
                                                    <Nav variant="pills" className={`du-tab-nav ${isRTL === "rtl" ? "du-tab-nav-rtl" : "du-tab-nav-ltr"}`}>
                                                        <Nav.Item>
                                                            <Nav.Link style={{paddingInlineStart:0}}eventKey="shelves">{t("SHELVES")}</Nav.Link>
                                                        </Nav.Item>
                                                        {/* <Nav.Item>
                                                            <Nav.Link eventKey="fields">{t("FIELDS")}</Nav.Link>
                                                        </Nav.Item>
                                                        <Nav.Item>
                                                            <Nav.Link eventKey="junctions">{t("JUNCTIONS")}</Nav.Link>
                                                        </Nav.Item> */}
                                                    </Nav>
                                                </Col>
                                            </Row>
                                            <Row className="du-tab-content-row mt-1">
                                                <Col xs="12">
                                                    <Tab.Content>
                                                        <Tab.Pane eventKey="shelves">
                                                            {/* Shelves content */}
                                                            <p className='du-tagline' style={{fontSize:16, direction: isRTL === 'rtl' ? 'rtl' : 'ltr'}}>
                                                                {selectedShelf != null && `${t('SHELF')} : S${selectedShelf?.rank}`}
                                                            </p>
                                                            {showConfigurationBox ? (
                                                                <ConfigurationBox
                                                                    isRTL = {isRTL}
                                                                    dmode = {dmode}
                                                                    displayUnitData={displayUnitData}
                                                                    selectedShelf = {selectedShelf}
                                                                    depthValue = {depthValue}
                                                                    setDepthValue = {setDepthValue}
                                                                    sspaceValue = {sspaceValue}
                                                                    setSSpaceValue = {setSSpaceValue}
                                                                    ffloorValue = {ffloorValue}
                                                                    setFFloorValue = {setFFloorValue}
                                                                    thicknessValue = {thicknessValue}
                                                                    setThicknessValue = {setThicknessValue}
                                                                    onShelfPropertyChange = {handleShelfPropertyChange}
                                                                    heightAdjustmentStep = {HEIGHT_ADJUSTMENT_STEP}
                                                                    gapAdjustmentStep = {GAP_ADJUSTMENT_STEP}
                                                                    onAddShelfAbove = {handleAddShelfAbove}
                                                                    onAddShelfBelow = {handleAddShelfBelow}
                                                                    onDeleteShelf = {handleDeleteShelf}
                                                                    displayRatio = {displayRatio}
                                                                />
                                                            
                                                            ) : (<EmptyDisplay 
                                                                    isRTL = {isRTL}
                                                                    dmode = {dmode}
                                                                />
                                                            )}
                                                        </Tab.Pane>
                                                        <Tab.Pane eventKey="fields">
                                                            {/* Fields content */}
                                                            <p className='du-tagline' style={{fontSize:10}}>Field F1</p>
                                                        
                                                        </Tab.Pane>
                                                        <Tab.Pane eventKey="junctions">
                                                            {/* Junctions content */}
                                                            <p className='du-tagline' style={{fontSize:10}}>Junction J1</p>
                                                        </Tab.Pane>
                                                    </Tab.Content>
                                                    {/* Save button */}
                                                </Col>
                                            </Row>
                                        </Tab.Container>
                                    </Col>
                                </Row>
                            </div>
                            <div className="du-right-footer" style={{display: 'flex',justifyContent: isRTL === 'rtl' ? 'flex-start' : 'flex-end'}}>
                                <Buttons
                                    className="du-save-btn"
                                    size="sm"
                                    clickEvent={handleSave}
                                    variant="success"
                                    dir={isRTL}
                                    btnLabel={t('btnnames.save') || 'Save'}
                                />
                            </div>
                        </div>
                    </div>
                </Offcanvas.Body>
            </Offcanvas>

            {/* Delete Confirmation Modal */}
            <FieldArrayConfirmModal
                show={showDeleteModal}
                setShow={setShowDeleteModal}
                data={deleteModalData}
                closeButtonLabel="btnnames.back"
                confirmButtonLabel="btnnames.yes"
            />

            {/* Save Confirmation Modal (for overflow products) */}
            <FieldArrayConfirmModal
                show={showSaveConfirmModal}
                setShow={setShowSaveConfirmModal}
                data={saveConfirmModalData}
                closeButtonLabel="btnnames.back"
                confirmButtonLabel="btnnames.yes"
            />

            {/* Unsaved Changes Confirmation Modal */}
            <FieldArrayConfirmModal
                show={showUnsavedChangesModal}
                setShow={setShowUnsavedChangesModal}
                data={unsavedChangesModalData}
                closeButtonLabel="btnnames.back"
                confirmButtonLabel="btnnames.yes"
            />
        </React.Fragment>
    )
}

export default DisplayUnitEdit;